import React from 'react';
import type { ImagePickerResponse } from 'react-native-image-picker';
import { err, ok } from 'neverthrow';

import { DropzoneFile } from '@rover/types/src/ImagePicker';

import { toJpeg } from '../../utils/imageMagickUtils';
import { Logger } from '../Logging';

import { fileContentsDoNotMatchExtension } from './fileVerification';
import {
  filesToMediaFiles,
  getSelectMediaDropzoneProps,
  getSelectMediaInputProps,
} from './internals.common';
import MediaFile from './MediaFile';
import MediaSelectionError, {
  MediaSelectionCorruptedFileError,
  MediaSelectionInvalidFileTypeError,
  MediaSelectionNoFilesError,
  MediaSelectionUnknownError,
} from './MediaSelectionError';
import { emitSelectionMetrics, emitSelectionRejected } from './metrics';
import {
  ImagePickerResizeResult,
  MediaType,
  SelectMediaOptions,
  SelectMediaResult,
  ShowActionSheetWithOptions,
} from './types';

/**
 * Web has two selection paths and they do different things to the bytes: the file
 * input transcodes images to JPEG, the dropzone hands the originals straight through.
 * Every metric below is tagged with which one produced it so the two stay separable.
 */
export const SelectionPaths = {
  INPUT: 'input',
  DROPZONE: 'dropzone',
} as const;

const filesToJpegFiles = async (files: MediaFile[]): Promise<MediaFile[]> => {
  const jpegFiles = await Promise.all(
    files.map(async (file) => {
      const jpegFile = await toJpeg(file);
      return new MediaFile(jpegFile);
    })
  );
  return jpegFiles;
};

function rejectSelection(
  error: MediaSelectionError,
  selectionPath: string,
  { report = true }: { report?: boolean } = {}
): SelectMediaResult {
  if (report) Logger.error(error);
  emitSelectionRejected(error, selectionPath);
  return err(error);
}

export const getFilesFromSelectionEvent = async (
  event: React.ChangeEvent<HTMLInputElement>
): Promise<SelectMediaResult> => {
  Logger.debug('rsdk.media.selectMedia.start');

  try {
    const { files } = event?.target || {};
    // ensure files exist
    if (!files?.length) {
      // In the case of a user selecting a file, then clicking the file input again,
      // then cancelling the dialog: Chrome will empty the input and fire a change event,
      // but Safari and Firefox will NOT empty the input or fire the change event.
      // This error is returned in the Chrome case.
      return rejectSelection(new MediaSelectionNoFilesError(), SelectionPaths.INPUT);
    }

    if (await fileContentsDoNotMatchExtension(Array.from(files))) {
      return rejectSelection(new MediaSelectionCorruptedFileError(), SelectionPaths.INPUT, {
        report: false,
      });
    }

    let imageMediaFiles: MediaFile[] = [];
    const videoMediaFiles: MediaFile[] = [];
    try {
      const mediaFiles = await filesToMediaFiles(files);

      mediaFiles.forEach((file) => {
        if (file.type === MediaType.IMAGE) {
          imageMediaFiles.push(file);
        } else videoMediaFiles.push(file);
      });

      // Convert all images to JPEG
      imageMediaFiles = await filesToJpegFiles(imageMediaFiles);

      // Uses the files as picked, since the transcode rewrites size and format.
      emitSelectionMetrics(mediaFiles, {
        selectionPath: SelectionPaths.INPUT,
        transcoded: true,
      });
    } catch {
      return rejectSelection(new MediaSelectionInvalidFileTypeError(), SelectionPaths.INPUT);
    }

    Logger.debug('rsdk.media.selectMedia.success');

    return ok([...imageMediaFiles, ...videoMediaFiles]);
  } catch (error) {
    const unknownError = new MediaSelectionUnknownError(error);
    Logger.error(Error(`Unknown error: ${error}`));
    emitSelectionRejected(unknownError, SelectionPaths.INPUT);

    return err(unknownError);
  }
};

export async function getFilesFromDropzoneDrop(
  acceptedFiles: DropzoneFile[],
  rejectedFiles: DropzoneFile[]
): Promise<SelectMediaResult> {
  Logger.debug('rsdk.media.selectMedia.start');

  // handle wrong file type case
  if (rejectedFiles.length && !acceptedFiles.length) {
    return rejectSelection(new MediaSelectionInvalidFileTypeError(), SelectionPaths.DROPZONE, {
      report: false,
    });
  }
  if (acceptedFiles.length) {
    if (await fileContentsDoNotMatchExtension(acceptedFiles)) {
      return rejectSelection(new MediaSelectionCorruptedFileError(), SelectionPaths.DROPZONE, {
        report: false,
      });
    }
    try {
      const mediaFiles = acceptedFiles.map((file) => new MediaFile(file));

      // Unlike the file input above, the dropzone uploads the original bytes as-is.
      emitSelectionMetrics(mediaFiles, {
        selectionPath: SelectionPaths.DROPZONE,
        transcoded: false,
      });

      Logger.debug('rsdk.media.selectMedia.success');
      return ok(mediaFiles);
    } catch (error) {
      return rejectSelection(new MediaSelectionUnknownError(error), SelectionPaths.DROPZONE, {
        report: false,
      });
    }
  }
  return rejectSelection(new MediaSelectionNoFilesError(), SelectionPaths.DROPZONE, {
    report: false,
  });
}

/* eslint-disable @typescript-eslint/no-unused-vars */
export function getFilesFromImagePickerResponse(response: ImagePickerResponse): SelectMediaResult {
  return err(new MediaSelectionUnknownError('Not implemented for web'));
}

export async function mediaSelectionActionSheet(
  showActionSheetWithOptions: ShowActionSheetWithOptions,
  options: SelectMediaOptions
): Promise<ImagePickerResizeResult> {
  return err(new MediaSelectionUnknownError('Not implemented for web'));
}
/* eslint-enable @typescript-eslint/no-unused-vars */

export { filesToMediaFiles, getSelectMediaInputProps, getSelectMediaDropzoneProps };
